import React, { useEffect, useRef, useState } from 'react';
import { mod } from './index';

// Drinking Contest — 15s rapid-tap with a stamina meter. Each tap drains a
// little; stamina regens slowly between taps. If stamina hits zero you
// "vomit" and lose half your score (and need a moment to recover before
// you can drink again). Higher CON = bigger stamina pool.

const GAME_DURATION_MS    = 15000;
const TAP_COST_BASE       = 12;          // stamina drained per tap
const REGEN_PER_SEC       = 18;          // stamina regen / sec
const VOMIT_RECOVERY_MS   = 1200;        // can't tap during this

function paramsForCon(myMod, theirMod) {
  // Equal CON: 100 stamina cap.
  // +2 mod: 130, +4 mod: 160. -2 mod: 80, -4: 65.
  const diff = myMod - theirMod;
  let cap = 100;
  if (diff >= 4)  cap = 160;
  else if (diff >= 2) cap = 130;
  else if (diff <= -4) cap = 65;
  else if (diff <= -2) cap = 80;
  return { staminaMax: cap };
}

export default {
  id: 'drinkingContest',
  label: 'Drinking Contest',
  stat: 'con',
  icon: '🍺',
  description: '15s tap-to-gulp. Pace yourself — empty stamina = vomit, halves your score. Higher CON = bigger pool.',
  paramsFromStats(myScore, theirScore) {
    return paramsForCon(mod(myScore), mod(theirScore));
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const staminaMax = params?.staminaMax || 100;
    const startedAt  = useRef(null);
    const lastTickAt = useRef(null);
    const submitted  = useRef(false);

    const [now,      setNow]      = useState(0);
    const [stamina,  setStamina]  = useState(staminaMax);
    const [score,    setScore]    = useState(0);
    const [vomits,   setVomits]   = useState(0);
    const [recoverUntil, setRecoverUntil] = useState(0);
    const [done,     setDone]     = useState(false);

    useEffect(() => {
      startedAt.current = Date.now();
      lastTickAt.current = Date.now();
      let raf;
      const tick = () => {
        const t       = Date.now();
        const dtSec   = Math.max(0, (t - lastTickAt.current) / 1000);
        lastTickAt.current = t;
        const elapsed = t - startedAt.current;
        if (elapsed >= GAME_DURATION_MS) {
          setNow(GAME_DURATION_MS);
          setDone(true);
          return;
        }
        setNow(elapsed);
        // Stamina regen
        setStamina((s) => Math.min(staminaMax, s + REGEN_PER_SEC * dtSec));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [staminaMax]);

    useEffect(() => {
      if (done && !submitted.current) {
        submitted.current = true;
        onScore(score);
      }
    }, [done, score, onScore]);

    const recovering = Date.now() < recoverUntil;

    const gulp = () => {
      if (done || recovering) return;
      const cost = TAP_COST_BASE;
      const next = stamina - cost;
      if (next <= 0) {
        // Vomit — half score, brief lockout
        setStamina(staminaMax * 0.6);
        setScore((s) => Math.floor(s / 2));
        setVomits((v) => v + 1);
        setRecoverUntil(Date.now() + VOMIT_RECOVERY_MS);
      } else {
        setStamina(next);
        setScore((s) => s + 1);
      }
    };

    const remainingS = Math.max(0, (GAME_DURATION_MS - now) / 1000);
    const staminaPct = Math.max(0, Math.min(1, stamina / staminaMax));

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Drinking Contest</div>
        <div style={styles.headerRow}>
          <span style={styles.timer}>⏳ {remainingS.toFixed(1)}s</span>
          <span style={styles.score}>🍺 {score}</span>
          {vomits > 0 && <span style={styles.vomits}>🤢 {vomits}</span>}
          {staminaMax !== 100 && (
            <span style={styles.mult}>{staminaMax} CON pool</span>
          )}
        </div>

        <div style={styles.staminaBar}>
          <div
            style={{
              ...styles.staminaFill,
              width: `${staminaPct * 100}%`,
              background: staminaPct > 0.5 ? '#5cb870' : staminaPct > 0.25 ? '#facc15' : '#c03333',
            }}
          />
          <span style={styles.staminaLabel}>STAMINA</span>
        </div>

        <button
          style={{ ...styles.gulpBtn, ...(recovering ? styles.gulpBtnRecovering : {}) }}
          onClick={gulp}
          onTouchStart={(e) => { e.preventDefault(); gulp(); }}
          disabled={done || recovering}
        >
          {done ? `Done — ${score}` : recovering ? '🤢 Recovering…' : '🍺 GULP'}
        </button>

        <div style={styles.hint}>Pace yourself. Tap too fast → vomit → lose half your score.</div>
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 16 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  headerRow: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, fontWeight: 700, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", flexWrap: 'wrap', justifyContent: 'center' },
  timer:     { background: '#0e1528', border: '1px solid #4a3d28', padding: '4px 10px', borderRadius: 4, color: '#9a8b6e' },
  score:     { background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', padding: '4px 10px', borderRadius: 4, color: '#d4a847' },
  vomits:    { background: 'rgba(140,200,80,0.15)', border: '1px solid #5cb870', padding: '4px 8px', borderRadius: 4, color: '#86efac', fontSize: 11 },
  mult:      { background: 'rgba(122,184,224,0.15)', border: '1px solid #7ab8e0', padding: '4px 8px', borderRadius: 4, color: '#7ab8e0', fontSize: 11 },
  staminaBar:{ position: 'relative', width: 'min(360px, 86vw)', height: 22, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, overflow: 'hidden' },
  staminaFill:{ position: 'absolute', top: 0, left: 0, height: '100%', transition: 'width 60ms linear, background 200ms' },
  staminaLabel:{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#1a0e00', fontWeight: 800, letterSpacing: '0.08em', textShadow: '0 1px 2px rgba(255,255,255,0.4)' },
  gulpBtn:   { width: 'min(360px, 86vw)', padding: '28px 0', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '2px solid #c4a44a', color: '#1a0e00', fontSize: 22, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 8, letterSpacing: '0.08em', boxShadow: '0 8px 24px rgba(212,168,71,0.4)', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' },
  gulpBtnRecovering: { background: '#3a1a1a', border: '2px solid #c03333', color: '#f87171', boxShadow: 'none', cursor: 'not-allowed' },
  hint:      { fontSize: 11, color: '#9a8b6e', fontStyle: 'italic', textAlign: 'center', maxWidth: 320 },
};
